"use client";

import { useEffect } from "react";
import type { CauthmodalState } from "./useCauthmodalState";

const ESCAPE_KEY = "Escape";

interface UseCauthmodalEscapeDismissInput {
  /** The gate's dismissal — the SAME callback the modal's close disc and its
   *  backdrop run (`AuthModalProvider.closeAuthModal`), so Escape releases a
   *  parked post-login request exactly like the X does. Absent = this instance
   *  has no dismissal, and Escape has nothing to run. */
  onClose?: () => void;
  authState: CauthmodalState;
}

/**
 * Escape dismisses the sign-in gate, through its own close.
 *
 * Every surface that raises the gate stands its Escape handling down while it
 * is up (the teams deck and the team overlay both check `isAuthModalOpen`) on
 * the stated law that the auth modal owns its own Escape — but nothing here
 * ever claimed it: `Cmodal` has no keyboard handling at all, so the key fell on
 * the floor and an abandoned gate kept its parked intent until some later
 * sign-in replayed it. This is that claim.
 *
 * Two things outrank it, in the ratified innermost-first order:
 * - A legal sheet opened from the gate is above it and runs its OWN Escape
 *   (`Crisingmodal`, gated on being the top registered modal). While one is up
 *   this handler stays silent, so a single Escape closes the sheet rather than
 *   taking the gate down with it.
 * - A sign-in the user already started — the magic-link request in flight, or
 *   a provider handoff mid-redirect — is not a gate waiting on a decision. The
 *   close disc stays available as the deliberate way out of it.
 */
export function useCauthmodalEscapeDismiss({
  onClose,
  authState,
}: UseCauthmodalEscapeDismissInput): void {
  const {
    loadingApple,
    loadingEmail,
    loadingGoogle,
    showPolicy,
    showTerms,
  } = authState;
  const legalSheetOpen = showTerms || showPolicy;
  const authRequestInFlight = loadingApple || loadingEmail || loadingGoogle;

  useEffect((): (() => void) | undefined => {
    if (onClose == null || legalSheetOpen || authRequestInFlight) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== ESCAPE_KEY) return;
      event.preventDefault();
      onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return (): void => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [authRequestInFlight, legalSheetOpen, onClose]);
}
